import React, { useState, useMemo } from 'react'
import { useParams, Outlet, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Stack, Typography, Box, IconButton, ToggleButtonGroup } from '@mui/material'

import { toast } from 'components/global/CUK/Toast/Toast'
import SlideInModal from 'components/Modal/SlideInModal'
import CardActionButton from 'components/global/CardActions/CardActionButton'
import { getTimezoneByValue, formatTimezoneDisplay } from 'utils/timezones'

import { useDetail } from '../use-event-ssids'
import { useDeactivate, useActivate, useGeneratePassphrase } from '../use-actions'
import { formatDate, formatTime, formatDuration, getEventStatus } from '../helpers'
import { Status } from '../Table/columns-config'
import ActionExport from './ActionExport'
import { StyledToggleButton } from './styles'
import CardSkeleton from './CardSkeleton'
import EventSsidForm from '../Forms/EventSsidForm'
import StatsTab from './Statistics/StatsTab'
import { trackEvent } from 'analytics'

const PassphraseField = ({ passphrase }) => {
  const [show, setShow] = useState(false)
  const { t } = useTranslation()

  const copyToClipboard = () => {
    navigator.clipboard.writeText(passphrase)
    toast.success(t('event_ssids.passphrase_copied'))
    trackEvent('wifi_management_passphrase_copied')
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        border: '1px solid #EFEFEF',
        borderRadius: '8px',
        padding: '6px 12px',
        height: '32px',
        width: '200px'
      }}
    >
      <Typography sx={{ fontSize: 12, flex: 1, fontFamily: 'monospace', color: '#656565', fontWeight: 400 }}>
        {show ? passphrase : '********'}
      </Typography>
      <Stack direction="row" gap={0.5}>
        <IconButton 
          size="small" 
          onClick={() => setShow(!show)} 
          sx={{ 
            color: '#656565', 
            p: 0,
            width: '16px',
            height: '16px',
            minWidth: '16px',
            minHeight: '16px'
          }}
        >
          <i className={`fa-solid fa-${show ? 'eye-slash' : 'eye'}`} style={{ fontSize: 10 }} />
        </IconButton>
        <IconButton 
          size="small" 
          onClick={copyToClipboard} 
          sx={{ 
            color: '#656565', 
            p: 0,
            width: '16px',
            height: '16px',
            minWidth: '16px',
            minHeight: '16px'
          }}
        >
          <i className="fa-regular fa-copy" style={{ fontSize: 10 }} />
        </IconButton>
      </Stack>
    </Box>
  )
}

const ActionDeactivate: React.FC<{ onError: (v: string | null) => void }> = ({ onError = () => { } }) => {
  const { id } = useParams()
  const [deactivate, { loading }] = useDeactivate()
  const { t } = useTranslation()

  const handleClick = async () => {
    onError(null)
    const { error } = await deactivate(id as string)
    if (error) {
      toast.error(t('event_ssids.deactivate.error'))
      onError(error as string)
    } else {
      toast.success(t('event_ssids.deactivate.success'))
      trackEvent('wifi_management_deactivated')
    }
  }

  return (
    <CardActionButton
      iconlabel={loading ? 'spinner fa-spin' : 'pause'}
      iconweight="solid"
      iconsize="xs"
      tooltiptitle={t('event_ssids.actions.deactivate')}
      onClick={handleClick}
      style={{
        border: '1px solid #EFEFEF',
        borderRadius: '8px',
        width: '24px',
        height: '24px',
        minWidth: '24px',
        minHeight: '24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#656565'
      }}
    />
  )
}

const ActionActivate: React.FC<{ onError: (v: string | null) => void }> = ({ onError = () => { } }) => {
  const { id } = useParams()
  const [activate, { loading }] = useActivate()
  const { t } = useTranslation()

  const handleClick = async () => {
    onError(null)
    const { error } = await activate(id as string)
    if (error) {
      toast.error(t('event_ssids.activate.error'))
      onError(error as string)
    } else {
      toast.success(t('event_ssids.activate.success'))
      trackEvent('wifi_management_activated')
    }
  }

  return (
    <CardActionButton
      iconlabel={loading ? 'spinner fa-spin' : 'play'}
      iconweight="solid"
      iconsize="xs"
      tooltiptitle={t('event_ssids.actions.activate')}
      onClick={handleClick}
      style={{
        border: '1px solid #EFEFEF',
        borderRadius: '8px',
        width: '24px',
        height: '24px',
        minWidth: '24px',
        minHeight: '24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#656565'
      }}
    />
  )
}

const ActionResetPassphrase: React.FC = () => {
  const { id } = useParams()
  const [generatePassphrase, { loading }] = useGeneratePassphrase()
  const { t } = useTranslation()
  const [isAnimating, setIsAnimating] = useState(false)

  const handleClick = async () => {
    setIsAnimating(true)
    const { error } = await generatePassphrase(id as string)
    
    setIsAnimating(false)
    
    if (error) {
      toast.error(t('event_ssids.generate_passphrase.error'))
    } else {
      toast.success(t('event_ssids.generate_passphrase.success'))
      trackEvent('wifi_management_passphrase_reset')
    }
  }

  return (
    <CardActionButton
      iconlabel={loading ? 'spinner fa-spin' : 'rotate'}
      iconweight="solid"
      iconsize="xs"
      tooltiptitle={t('event_ssids.actions.reset_passphrase')}
      onClick={handleClick}
      disabled={loading}
      style={{
        border: '1px solid #EFEFEF',
        borderRadius: '8px',
        width: '24px',
        height: '24px',
        minWidth: '24px',
        minHeight: '24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#656565',
        transition: 'transform 0.3s ease-in-out',
        transform: isAnimating ? 'rotate(360deg)' : 'rotate(0deg)'
      }}
    />
  )
}

const InfoLine = ({ title, value }) => (
  <Stack
    direction="row"
    sx={{
      py: 1,
      px: 2,
      justifyContent: 'space-between',
      alignItems: 'center',
      position: 'relative'
    }}
  >
    <Typography sx={{ fontSize: 12, color: '#656565', fontWeight: 600 }}>{title}</Typography>
    <Box 
      sx={{ 
        flex: 1, 
        height: '1px', 
        backgroundColor: '#F7F7F7',
        mx: 2
      }} 
    />
    <Typography sx={{ 
      fontSize: 12, 
      color: '#656565', 
      fontWeight: 400,
      textAlign: 'right'
    }}>
      {value}
    </Typography>
  </Stack>
)

const Card: React.FC = () => {
  const { t } = useTranslation()
  const [errorAction, setErrorAction] = useState<string | null>(null)
  const navigate = useNavigate()
  const [selectedTab, setSelectedTab] = useState('informations')
  const [showEditModal, setShowEditModal] = useState(false)
  const { loading, item } = useDetail()

  const duration = useMemo(() => {
    if (!item) return ''
    return formatDuration(item, t)
  }, [item, t])

  const isFinished = useMemo(() => {
    if (!item) return false
    const { isFinished } = getEventStatus(item)
    return isFinished
  }, [item])

  const timezoneDisplay = useMemo(() => {
    if (!item) return ''
    const timezone = getTimezoneByValue(item.utc_offset)
    return timezone ? formatTimezoneDisplay(timezone, t) : item.utc_offset
  }, [item, t])

  if (!item && !loading) return null

  return (
    <>
      <SlideInModal
        onClose={() => navigate('..')}
        hideHeader
        noPadding
        width="480px"
      >
        {loading ? (
          <CardSkeleton />
        ) : (
          <Stack sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Stack 
              direction="row" 
              alignItems="center" 
              justifyContent="space-between"
              sx={{ px: 3, pt: 3, pb: 2 }}
            >
              <Stack direction="row" gap={1.5}>
                {!isFinished && (
                  <>
                    <Box
                      sx={{
                        width: '24px',
                        height: '24px',
                        minWidth: '24px',
                        minHeight: '24px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '50%',
                        backgroundColor: '#F7F7F7',
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: '#EFEFEF'
                        }
                      }}
                      onClick={() => {
                        trackEvent('wifi_management_edit')
                        setShowEditModal(true)
                      }}
                    >
                      <i 
                        className="fa-regular fa-pen" 
                        style={{ fontSize: 12, color: '#464646' }}
                      />
                    </Box>
                    <Box
                      sx={{
                        width: '24px',
                        height: '24px',
                        minWidth: '24px',
                        minHeight: '24px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '50%',
                        backgroundColor: '#F7F7F7',
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: '#EFEFEF'
                        }
                      }}
                      onClick={() => navigate('./remove')}
                    >
                      <i 
                        className="fa-regular fa-trash" 
                        style={{ fontSize: 12, color: '#464646' }}
                      />
                    </Box>
                  </>
                )}
              </Stack>

              <Box sx={{ height: '24px', position: 'absolute', left: '50%', transform: 'translateX(calc(-50% - 16px))' }}>
                {item && <Status item={item} />}
              </Box>

              <IconButton 
                onClick={() => navigate('..')} 
                sx={{ 
                  p: 0,
                  width: '24px',
                  height: '24px',
                  minWidth: '24px',
                  minHeight: '24px',
                  borderRadius: '50%',
                  '&:hover .close-icon': {
                    backgroundColor: '#F7F7F7'
                  }
                }}
              >
                <Box
                  className="close-icon"
                  sx={{
                    width: '24px',
                    height: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '50%',
                    transition: 'background-color 0.2s'
                  }}
                >
                  <i className="fa-light fa-xmark" style={{ fontSize: 12, color: '#464646' }} />
                </Box>
              </IconButton>
            </Stack>

            <Stack gap={2} alignItems="center" sx={{ px: 3 }}>
              <Typography sx={{ fontSize: 16, fontWeight: 600, textAlign: 'center', color: '#464646' }}>
                {item?.label} - {item?.name}
              </Typography>

              <ToggleButtonGroup
                value={selectedTab}
                exclusive
                onChange={(event, newTab) => {
                  if (newTab !== null) {
                    trackEvent('wifi_management_tab_changed', {
                      tab_name: newTab
                    })
                    setSelectedTab(newTab)
                  }
                }}
                sx={{
                  width: '100%',
                  border: '2px solid #EFEFEF',
                  borderRadius: '8px',
                  background: '#fff',
                  position: 'relative',
                }}
              >
                <StyledToggleButton value="informations">
                  {t('event_ssids.tabs.informations')}
                </StyledToggleButton>
                <StyledToggleButton value="statistics">
                  {t('event_ssids.tabs.statistics')}
                </StyledToggleButton>
              </ToggleButtonGroup>
            </Stack>

            <Box 
              sx={{ 
                px: 3,
                pb: 3,
                pt: 3,
                overflowY: 'auto',
                flex: 1,
                '&::-webkit-scrollbar': {
                  display: 'none'
                },
                scrollbarWidth: 'none',
                msOverflowStyle: 'none'
              }}
            >
              {selectedTab === 'informations' && item && (
                <Stack gap={2}>
                  {Boolean(errorAction) && (
                    <Typography sx={{ fontSize: 12, fontWeight: 500, color: 'error.main', textAlign: 'center' }}>
                      {t(`errors.event_ssids.${errorAction}`)}
                    </Typography>
                  )}

                  <Stack 
                    sx={{ 
                      borderRadius: '8px', 
                      pb: 1,
                      px: 2,
                      gap: 2
                    }}
                  >
                    <Stack direction="row" gap={1} alignItems="center" justifyContent="center">
                      <Typography sx={{ fontSize: 12, color: '#656565', fontWeight: 600 }}>SSID :</Typography>
                      <Typography sx={{ fontSize: 12, color: '#656565', fontWeight: 500 }}>
                        {item.name}
                      </Typography>
                    </Stack>

                    <Stack direction="row" gap={1} alignItems="center" justifyContent="center">
                      <Typography sx={{ fontSize: 12, color: '#656565', fontWeight: 600 }}>
                        {t('event_ssids.card.passphrase')} :
                      </Typography>
                      <PassphraseField passphrase={item.passphrase} />
                    </Stack>
                  </Stack>

                  {!isFinished && (
                    <Stack direction="row" gap={1} justifyContent="center">
                      <ActionExport ssid={item} analyticsSource="wifi_management" />
                      {item.enabled ? (
                        <ActionDeactivate onError={setErrorAction} />
                      ) : (
                        <ActionActivate onError={setErrorAction} />
                      )}
                      <CardActionButton
                        iconlabel="list-timeline"
                        iconweight="solid"
                        iconsize="xs"
                        tooltiptitle={t('event_ssids.actions.history_coming_soon')}
                        disabled
                        style={{
                          border: '1px solid #EFEFEF',
                          borderRadius: '8px',
                          width: '24px',
                          height: '24px',
                          minWidth: '24px',
                          minHeight: '24px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#ABABAB',
                          opacity: 0.5
                        }}
                      />
                      <ActionResetPassphrase />
                    </Stack>
                  )}

                  <Stack sx={{ mt: 1 }}>
                    <InfoLine title={t('event_ssids.card.connected_devices')} value="-" />
                    <InfoLine
                      title={t('event_ssids.card.created_by')}
                      value={item.created_by?.email || '-'}
                    />
                    <InfoLine
                      title={t('event_ssids.card.creation_date')}
                      value={formatDate(item.created_at)}
                    />
                    <InfoLine
                      title={t('event_ssids.card.start_date')}
                      value={`${formatDate(item.start_date)} - ${formatTime(item.start_time)}`}
                    />
                    <InfoLine
                      title={t('event_ssids.card.end_date')}
                      value={`${formatDate(item.end_date)} - ${formatTime(item.end_time)}`}
                    />
                    <InfoLine title={t('event_ssids.card.duration')} value={duration} />
                    <InfoLine title={t('event_ssids.card.timezone')} value={timezoneDisplay} />
                  </Stack>
                </Stack>
              )}

              {selectedTab === 'statistics' && (
                <StatsTab />
              )}
            </Box>
          </Stack>
        )}
      </SlideInModal>
      
      {showEditModal && (
        <EventSsidForm 
          onClose={() => setShowEditModal(false)} 
          fromCard={true}
        />
      )}
      
      <Outlet />
    </>
  )
}

export default Card
